import store from '~/store'
import Cookies from 'js-cookie'
import { QrValidationService } from '~/services/qrValidation'
import dynamicQRHandler from '~/utils/dynamicQRHandler'
import indexedDBManager from '~/utils/indexedDBManager'
import logger from '~/utils/logger'

export async function qrValidationGuard(to, from, next) {
  // Check if QR scanning is globally enabled before processing any QR routes
  if (to.name === 'qr-dynamic') {
    try {
      const statusResponse = await fetch('/api/qr-scanning-status', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      })

      const statusData = await statusResponse.json()

      if (!statusData.enabled) {
        // QR scanning is disabled globally, redirect to disabled page
        logger.log('QR scanning is disabled, redirecting to qr-disabled page')
        return next({ name: 'qr-disabled' })
      }
    } catch (error) {
      logger.error('Error checking QR scanning status:', error)
      // On error, continue with normal flow (fail gracefully)
    }
  }

  // Handle dynamic QR route - validate against IndexedDB and server
  if (to.name === 'qr-dynamic') {
    const dynamicHash = to.params.dynamicHash

    logger.log('Dynamic QR route guard - from:', from?.name, 'to:', to.name, 'hash:', dynamicHash)

    try {
      // Dynamic QR handler should already be initialized in app.js
      // If not yet initialized, initialize it now (fallback)
      if (!dynamicQRHandler.isInitialized) {
        await dynamicQRHandler.init()
      }

      // Check for legitimate access token (set by handleStartNow)
      const legitimateAccess = sessionStorage.getItem('legitimateQRAccess')
      const accessTimestamp = sessionStorage.getItem('qrAccessTimestamp')

      // Validate access token - must match hash and be recent (within 5 minutes)
      const isLegitimateAccess = legitimateAccess === dynamicHash &&
                                 accessTimestamp &&
                                 (Date.now() - parseInt(accessTimestamp)) < 300000 // 5 minutes

      if (isLegitimateAccess) {
        logger.log('Legitimate access detected via Start Now button:', dynamicHash)
        // Clear the access token after use
        sessionStorage.removeItem('legitimateQRAccess')
        sessionStorage.removeItem('qrAccessTimestamp')

        // Set the dynamic hash for the wizard
        Cookies.set('slug', dynamicHash)
        store.dispatch('wizard/saveQrcode', { qrcode: dynamicHash })

        // Continue to QR page
        next()
        return
      }

      // Check if we have the QR data already stored
      const storedQR = await indexedDBManager.getQRData(dynamicHash)
      const isNewlyCreated = storedQR && storedQR.additional_data && storedQR.additional_data.is_rescan

      logger.log('Validation check - dynamicHash:', dynamicHash, 'hasStoredQR:', !!storedQR, 'isNewlyCreated:', isNewlyCreated)

      // CRITICAL: ALWAYS check status first, regardless of skipQRValidation flag
      // If QR exists in IndexedDB, check if it's completed/scanned/invalid before allowing access
      if (storedQR) {
        logger.log('QR found in IndexedDB:', dynamicHash, 'status:', storedQR.status)

        // Check if QR is completed - redirect to welcome page
        if (storedQR.status === 'completed') {
          logger.log('QR is completed, redirecting to welcome page:', dynamicHash)
          // Clear any skipQRValidation flag to prevent bypass attempts
          sessionStorage.removeItem('skipQRValidation')
          return next({ name: 'welcome' })
        }

        // Check if QR is scanned - redirect to welcome page
        if (storedQR.status === 'scanned') {
          logger.log('QR is scanned (used), redirecting to welcome page:', dynamicHash)
          // Clear any skipQRValidation flag to prevent bypass attempts
          sessionStorage.removeItem('skipQRValidation')
          return next({ name: 'welcome' })
        }

        // Check if QR is invalid - redirect to welcome page
        if (storedQR.status === 'invalid') {
          logger.log('QR is invalid, redirecting to welcome page:', dynamicHash)
          // Clear any skipQRValidation flag to prevent bypass attempts
          sessionStorage.removeItem('skipQRValidation')
          return next({ name: 'welcome' })
        }

        logger.log('QR is active, checking if validation should be skipped...')

        // NOW check if we should skip server validation (for newly created QRs only)
        // This only skips the server API call, NOT the status checks above
        const skipValidationHash = sessionStorage.getItem('skipQRValidation')
        const shouldSkipValidation = skipValidationHash === dynamicHash

        if (shouldSkipValidation) {
          logger.log('Skipping SERVER validation for newly created QR (status checks already passed):', dynamicHash)

          // Don't clear the flag here - let the component handle it
          // This ensures the component also knows to skip validation

          // Set the dynamic hash for the wizard
          Cookies.set('slug', dynamicHash)
          store.dispatch('wizard/saveQrcode', { qrcode: dynamicHash })

          // Continue to QR page
          next()
          return
        }

        // QR is active but no skipQRValidation flag - allow access normally
        logger.log('QR is active, allowing access:', dynamicHash)

        // Set the dynamic hash for the wizard
        Cookies.set('slug', dynamicHash)
        store.dispatch('wizard/saveQrcode', { qrcode: dynamicHash })

        // Continue to QR page
        next()
        return
      }

      // No legitimate access token and QR not in IndexedDB - this is unauthorized direct access
      logger.warn('Unauthorized direct access attempt to QR page:', dynamicHash)
      logger.log('Redirecting to welcome page - user must use Start Now button')

      // Redirect to welcome page
      return next({ name: 'welcome' })
      
    } catch (error) {
      logger.error('Dynamic QR validation failed:', error)
      return next({ name: 'welcome' })
    }
    
    return
  }
  
  // For all other routes, continue normally
  next()
}

export default qrValidationGuard